import {
  type InfiniteData,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

import { useAgentExecutionClient } from "src/api/agents/execution";
import {
  type AgentInvokeResponse,
  type Data,
  Environment,
  ExecutionMode,
  HumanMessageTypeEnum,
  type SessionMessagesResponse,
  type SessionMessagesResponseMessagesInner,
  SessionStatus,
} from "src/clients/agent-management";
import { toastActions } from "src/design-system/Toast/utils";

type UseCaseCopilotInvokeParams = {
  agentId: string | undefined;
  agentVersionId: string | undefined;
  caseId: string;
  sessionId: string | null;
  environment: string;
  fileIds: string[];
  messagesQueryKey: readonly unknown[];
  onInvokeStarted: () => void;
};

export const useCaseCopilotInvoke = ({
  agentId,
  agentVersionId,
  caseId,
  sessionId,
  environment,
  fileIds,
  messagesQueryKey,
  onInvokeStarted,
}: UseCaseCopilotInvokeParams) => {
  const { client } = useAgentExecutionClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      invocationMessage: string,
    ): Promise<AgentInvokeResponse> => {
      if (!client) {
        throw new Error("Agent execution client not available");
      }
      if (!agentId) {
        throw new Error("No copilot agent configured for this case template");
      }
      if (!sessionId) {
        throw new Error("Session ID not available - user ID is required");
      }

      // Cast needed: generated `Data` type merges all invoke variants,
      // making playground/flow-only fields incorrectly required.
      const data = {
        agent_id: agentId,
        agent_version_id: agentVersionId ?? "latest",
        case_id: caseId,
        session_id: sessionId,
        invocation_message: invocationMessage,
        file_ids: fileIds,
        type: "invoke",
        source: "case",
        control: {
          execution_mode: ExecutionMode.ASYNC,
          environment:
            environment === "live"
              ? Environment.PRODUCTION
              : Environment.SANDBOX,
        },
      } as unknown as Data;

      const response =
        await client.invokeAgentCopilotsManagementApiV0InvokePost({
          data,
        });
      return response.data;
    },
    onMutate: async (invocationMessage: string) => {
      await queryClient.cancelQueries({ queryKey: messagesQueryKey });

      const previousData =
        queryClient.getQueryData<InfiniteData<SessionMessagesResponse>>(
          messagesQueryKey,
        );

      queryClient.setQueryData<InfiniteData<SessionMessagesResponse>>(
        messagesQueryKey,
        (old) => {
          const firstPage = old?.pages[0];
          const optimisticFirstPage: SessionMessagesResponse = {
            session: firstPage?.session
              ? { ...firstPage.session, status: SessionStatus.RUNNING }
              : firstPage?.session,
            messages: [
              {
                type: HumanMessageTypeEnum.HUMAN,
                invocation_message: invocationMessage,
                content: invocationMessage,
              } as SessionMessagesResponseMessagesInner,
              ...(firstPage?.messages ?? []),
            ],
          } as SessionMessagesResponse;

          return {
            pages: [optimisticFirstPage, ...(old?.pages.slice(1) ?? [])],
            pageParams: old?.pageParams ?? [undefined],
          };
        },
      );

      return { previousData };
    },
    onSuccess: () => {
      onInvokeStarted();
    },
    onError: (_err, _vars, context) => {
      if (context?.previousData !== undefined) {
        queryClient.setQueryData(messagesQueryKey, context.previousData);
      }
      toastActions.failure({
        title: "Failed to send message",
        description: "Please try again",
      });
    },
    // No onSettled — polling picks up server data; forced refetch would race with optimistic update.
  });
};
